import {
  ArrowHelper,
  Group,
  Material,
  Mesh,
  MeshBasicMaterial,
  Object3D,
  SphereGeometry,
  Vector3,
} from 'three';

/** Shared palette aligned with UI primary tokens (`index.css`). */
export const SEGMENTATION_VISUAL = {
  primary: 0x4b86e6,
  primaryBright: 0x73a2ec,
  primaryDeep: 0x1d4ed8,
  accent: 0x93c5fd,
  white: 0xf8fbff,

  splineTube: 0x4b86e6,
  splineTubeOpacity: 0.78,
  splineTubeRadius: 0.065,
  splineDrawPreview: 0x93c5fd,
  splineDrawPreviewOpacity: 0.55,

  controlPointCore: 0xf8fbff,
  controlPointHalo: 0x4b86e6,
  controlPointCoreRadius: 0.11,
  controlPointHaloRadius: 0.19,
  controlPointHaloOpacity: 0.3,

  anchorCore: 0xf8fbff,
  anchorHalo: 0x4b86e6,
  anchorCoreRadius: 0.13,
  anchorHaloRadius: 0.26,
  anchorHaloOpacity: 0.24,
  anchorArrowColor: 0x73a2ec,
  anchorArrowLength: 2.4,
  anchorArrowHeadLength: 0.32,
  anchorArrowHeadWidth: 0.2,
} as const;

export function disposeObject3D(root: Object3D): void {
  root.traverse((obj) => {
    if (obj instanceof Mesh) {
      obj.geometry?.dispose();
      const mat = obj.material;
      if (Array.isArray(mat)) mat.forEach((m) => m.dispose());
      else mat?.dispose();
    }
  });
}

function createGlowSphere(
  radius: number,
  color: number,
  opacity: number,
  depthWrite = true,
): Mesh {
  return new Mesh(
    new SphereGeometry(radius, 24, 24),
    new MeshBasicMaterial({
      color,
      transparent: opacity < 1,
      opacity,
      depthWrite,
    }),
  );
}

export function createAnchorMarker(
  position: Vector3,
  cameraPosition: Vector3,
): { marker: Group; arrow: ArrowHelper } {
  const marker = new Group();
  marker.position.copy(position);

  marker.add(createGlowSphere(SEGMENTATION_VISUAL.anchorCoreRadius, SEGMENTATION_VISUAL.anchorCore, 1));
  marker.add(
    createGlowSphere(
      SEGMENTATION_VISUAL.anchorHaloRadius,
      SEGMENTATION_VISUAL.anchorHalo,
      SEGMENTATION_VISUAL.anchorHaloOpacity,
      false,
    ),
  );

  const toCamera = cameraPosition.clone().sub(position);
  const arrowDirection = toCamera.lengthSq() > 1e-8 ? toCamera.normalize() : new Vector3(0, 0, 1);
  const arrowLength = Math.min(
    SEGMENTATION_VISUAL.anchorArrowLength,
    Math.max(1.4, position.distanceTo(cameraPosition) * 0.14),
  );

  const arrow = new ArrowHelper(
    arrowDirection,
    position,
    arrowLength,
    SEGMENTATION_VISUAL.anchorArrowColor,
    SEGMENTATION_VISUAL.anchorArrowHeadLength,
    SEGMENTATION_VISUAL.anchorArrowHeadWidth,
  );
  arrow.line.material = new MeshBasicMaterial({
    color: SEGMENTATION_VISUAL.anchorArrowColor,
    transparent: true,
    opacity: 0.85,
    depthWrite: false,
  });
  arrow.cone.material = new MeshBasicMaterial({
    color: SEGMENTATION_VISUAL.white,
    transparent: true,
    opacity: 0.95,
  });

  return { marker, arrow };
}

export function createControlPointHalo(): Mesh {
  return createGlowSphere(
    SEGMENTATION_VISUAL.controlPointHaloRadius,
    SEGMENTATION_VISUAL.controlPointHalo,
    SEGMENTATION_VISUAL.controlPointHaloOpacity,
    false,
  );
}

export function disposeMaterial(material: Material | Material[]): void {
  if (Array.isArray(material)) material.forEach((m) => m.dispose());
  else material.dispose();
}
